import LicensedModule from "models/LicensedModule";
import { Answer } from "models/Answer";
import BaseService from "./BaseService";
import RootStore from "stores/RootStore";
import { AuthStore } from "stores/AuthStore";
import { fetch_retry } from "utils/FetchRetry";
import { ModulePreview } from "components/Admin/Organizations/types/Module.type";

export class ModuleService extends BaseService {
    protected urlBase: string = "api/Modules";

    public async getLicensedModule(LicensedModuleId: string) {
        const res = await this.get<LicensedModule>(
            new URLSearchParams({ LicensedModuleId: LicensedModuleId }),
            "GetLicensedModule",
        );
        return res;
    }

    public async getAllLicensedModuleTypes(
        organizationId: string,
        evaluationId?: string,
        licensedModuleIds?: string[],
    ) {
        const body = JSON.stringify({
            organizationId,
            evaluationId: evaluationId ?? "",
            licensedModuleIds: licensedModuleIds ?? [],
        });

        const res = await this.post<LicensedModule[]>(
            body,
            new URLSearchParams({}),
            "GetAllLicensedModuleTypes",
        );

        return res;
    }

    public async getLicensedModuleByTypeName(
        organizationId: string,
        typeName: string,
    ) {
        const res = await this.get<LicensedModule>(
            new URLSearchParams({
                OrganizationId: organizationId,
                ModuleTypeName: typeName,
            }),
            "GetLicensedModuleType",
        );
        return res;
    }

    public async getLicensedModules(): Promise<
        { id: string; title: string }[]
    > {
        return this.get(new URLSearchParams(), "GetLicensedModules");
    }

    public async getHierarchyLicensedModules(): Promise<
        { id: string; title: string }[]
    > {
        return this.get(new URLSearchParams(), "GetHierarchyLicensedModules");
    }

    public async getDataLabelingLicensedModules(): Promise<
        { id: string; name: string }[]
    > {
        return this.get(
            new URLSearchParams(),
            "GetDataLabelingLicensedModules",
        );
    }

    public async getConversationLicensedModules(): Promise<ModulePreview[]> {
        return this.get(
            new URLSearchParams(),
            "GetConversationLicensedModules",
        );
    }

    public async getQuestionResultLicensedModules(): Promise<ModulePreview[]> {
        return this.get(
            new URLSearchParams(),
            "GetQuestionResultLicensedModules",
        );
    }

    public async generateAutofillAnswersForModule(
        amdId: string,
        licensedModuleId: string,
    ): Promise<Answer[]> {
        return this.get(
            new URLSearchParams({ amdId, licensedModuleId }),
            "GenerateAutofillAnswersForModule",
        );
    }

    /**
     * Given the licensed module id of a lm that has conversation modules and a question
     * returns the count of answer tags grouped by distinct tags
     */
    public async getLabeledConversationCounts(
        licensedModuleId: string,
        questionId: string,
        abortSignal?: AbortSignal,
    ): Promise<{ id: string; value: string; count: number }[]> {
        return this.get(
            new URLSearchParams({ licensedModuleId, questionId }),
            "GetLabeledConversationsCountByTag",
            abortSignal,
        );
    }

    public async TryGetDataLabelingLock(
        amdId: string,
        licensedModuleId: string,
    ): Promise<boolean> {
        return this.get(
            new URLSearchParams({ amdId, licensedModuleId }),
            "GetDataLabelingLock",
        );
    }

    public async ReleaseDataLabelingLock(
        amdId: string,
        licensedModuleId: string,
    ) {
        const urlParams = new URLSearchParams({ amdId, licensedModuleId });
        let headers = RootStore().getStore(AuthStore).getRequestHeader();
        headers = { ...headers, "api-version": this.apiVersion };

        // Using fetch directly because the base service will debounce the request
        // and the browser may cancel it when unloading the page

        return fetch_retry(
            `/${this.urlBase}/ReleaseDataLabelingLock?${urlParams}`,
            {
                method: "DELETE",
                headers,
                keepalive: true,
            },
            0,
        );
    }
}
